import i18n from 'src/i18n';
import { hasValue } from 'src/shared/utils/common';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';

import { showCustomErrorMessage } from '../../components/show-custom-error-message';
import type { FormStore } from '../form.entity';
import type { GeologicalLoadService } from '../geological-load/geological-load-service';

import type { RigOperationSection } from './rig-operation-section.entity';
import { RigOperationStage } from './rig-operation-stage.entity';
import type { RigOperation } from './rig-operation.entity';
import type { RigOperationsService } from './rig-operations-service';

export class RigOperationsValidation {
  private readonly rigOperationsService: RigOperationsService;
  private readonly geoLoadService: GeologicalLoadService;
  private readonly notificationsService: NotificationsStore;
  private readonly formStore: FormStore;

  constructor(
    notificationsService: NotificationsStore,
    formStore: FormStore,
    rigOperationsService: RigOperationsService,
    geoLoadService: GeologicalLoadService,
  ) {
    this.rigOperationsService = rigOperationsService;
    this.geoLoadService = geoLoadService;
    this.notificationsService = notificationsService;
    this.formStore = formStore;
  }

  validateFormBeforeRestoreRigOperationsToDirectoryValues(
    rigOperation: RigOperation,
    shouldCalculateCSandPVPerRigOperation: boolean,
  ): boolean {
    let isValid = true;

    const errors: string[] = [];
    const firstStage = rigOperation.stages[0];

    if (!firstStage) {
      errors.push(i18n.t('newWellForm:Errors.requiredStageAndStartDate'));
      isValid = false;
    }

    if (firstStage && !firstStage.startDate) {
      errors.push(i18n.t('newWellForm:Errors.requiredStartDateForFirstStageForRestoreToDirectoryValues'));
      isValid = false;
    }

    if (!rigOperation.rigId) {
      errors.push(i18n.t('newWellForm:Errors.selectRig'));
      isValid = false;
    }

    if (shouldCalculateCSandPVPerRigOperation) {
      const { block, field, fluidType, wellType, wellPurpose } = this.formStore.fields;

      if (!block?.value || !field?.value) {
        errors.push(i18n.t('newWellForm:Errors.selectPad'));
        isValid = false;
      }

      if (!wellPurpose?.value) {
        errors.push(i18n.t('newWellForm:Errors.selectWellType'));
        isValid = false;
      }

      if (!fluidType?.value) {
        errors.push(i18n.t('newWellForm:Errors.selectWellPurpose'));
        isValid = false;
      }

      if (!wellType?.value) {
        errors.push(i18n.t('newWellForm:Errors.selectWellConstruction'));
        isValid = false;
      }
    } else {
      const { commercialSpeed, passingValue } = this.formStore.fields;

      if (!commercialSpeed?.value) {
        errors.push(i18n.t('newWellForm:Errors.enterCommercialSpeed'));
        isValid = false;
      }

      if (!passingValue?.value) {
        errors.push(i18n.t('newWellForm:Errors.enterPassing'));
        isValid = false;
      }
    }

    if (errors.length) {
      showCustomErrorMessage('newWellForm:Errors.restoreToDirectoryValuesSteps', errors.reverse(), 0);
    }

    return isValid;
  }

  validateStartDate(rigOperation: RigOperation, stage: RigOperationStage, section?: RigOperationSection): boolean {
    let isValid = true;

    const source = section ?? stage;

    if (!source.startDate) {
      isValid = false;

      source.addError('startDate', i18n.t('errors:requiredField'));
    }

    if (hasValue(source.startDate) && hasValue(source.endDate) && source.startDate > source.endDate) {
      isValid = false;

      source.addError('startDate', i18n.t('errors:startDateCannotBeGreaterThanEndDate'));
    }

    return isValid;
  }

  validateEndDate(rigOperation: RigOperation, stage: RigOperationStage, section?: RigOperationSection): boolean {
    let isValid = true;

    const source = section ?? stage;

    if (!source.endDate) {
      isValid = false;

      source.addError('endDate', i18n.t('errors:requiredField'));
    }

    if (hasValue(source.startDate) && hasValue(source.endDate) && source.startDate > source.endDate) {
      isValid = false;

      source.addError('endDate', i18n.t('errors:endDateCannotBeLessThanStartDate'));
    }

    return isValid;
  }

  validatePassingValueMinField(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section: RigOperationSection,
  ): boolean {
    let isValid = true;

    if (
      hasValue(section.passingValueMin) &&
      hasValue(section.passingValueMax) &&
      section.passingValueMin > section.passingValueMax
    ) {
      isValid = false;

      section.addError('passingValueMin', i18n.t('errors:startIntervalCannotBeGreaterThanEndInterval'));
    }

    return isValid;
  }
  validatePassingValueMaxField(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section: RigOperationSection,
  ): boolean {
    let isValid = true;

    if (
      hasValue(section.passingValueMin) &&
      hasValue(section.passingValueMax) &&
      section.passingValueMin > section.passingValueMax
    ) {
      isValid = false;

      section.addError('passingValueMin', i18n.t('errors:endIntervalCannotBeLessThanStartInterval'));
    }

    return isValid;
  }

  validatePassingValueField(
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section?: RigOperationSection,
  ): boolean {
    let isValid = true;

    const source = section ?? stage;

    if (
      source instanceof RigOperationStage &&
      (source.sections.length > 0 ||
        !this.rigOperationsService.isDrillingStage(source.wellLifeCycleStageId) ||
        this.rigOperationsService.rigOperations.length < 2)
    ) {
      return true;
    }

    if (!hasValue(source.passingValue)) {
      isValid = false;

      source.addError('passingValue', i18n.t('errors:requiredField'));
    } else if (source.passingValue <= 0) {
      isValid = false;

      source.addError('passingValue', i18n.t('newWellForm:Errors.passingCannotBeLessThanZero'));
    }

    return isValid;
  }

  validateDurationField(rigOperation: RigOperation, stage: RigOperationStage, section?: RigOperationSection): boolean {
    let isValid = true;
    const source = section ?? stage;

    if (source instanceof RigOperationStage && source.sections.length > 0) {
      return true;
    }

    if (!hasValue(source.duration)) {
      isValid = false;

      source.addError('duration', i18n.t('errors:requiredField'));
    } else if (source.duration <= 0) {
      isValid = false;

      source.addError('duration', i18n.t('newWellForm:Errors.durationCannotBeLessThanZero'));
    } else if (this.rigOperationsService.isDrillingStage(stage.wellLifeCycleStageId)) {
      const sourceGeoLoadDuration = ((): number => {
        const rigOperationGeoLoad = this.geoLoadService.geoLoadRigOperationsByRigOperationIdMap.get(
          rigOperation.entityId,
        );
        const stageGeoLoad = rigOperationGeoLoad?.stages.find((st) => st.stageEntityId === stage.entityId);
        const sectionGeoLoad = section
          ? stageGeoLoad?.sections.find((sec) => sec.sectionEntityId === section.entityId)
          : null;

        if (!stageGeoLoad || (section && !sectionGeoLoad)) {
          isValid = false;

          if (section && !sectionGeoLoad) {
            section.addError('duration', i18n.t('newWellForm:Errors.cannotLinkSectionToGeologicalLoad'));
          }

          if (!stageGeoLoad) {
            stage.addError('duration', i18n.t('newWellForm:Errors.cannotLinkStageToGeologicalLoad'));
          }

          return 0;
        }

        const source = (sectionGeoLoad ?? stageGeoLoad).geologicalLoad;

        return (
          (source.coreSamplingDayAmount ?? 0) +
          (source.daysAmountOfDataInterpretationOperation ?? 0) +
          (source.testingFormationWorkDayAmount ?? 0)
        );
      })();

      if (sourceGeoLoadDuration > source.duration) {
        isValid = false;

        source.addError('duration', i18n.t('newWellForm:Errors.durationCannotBeLessThanGeologicalLoad'));
      }
    }

    return isValid;
  }

  validateSectionType(rigOperation: RigOperation, stage: RigOperationStage, section: RigOperationSection) {
    let isValid = true;

    if (!section.sectionTypeId) {
      isValid = false;

      section.addError('sectionTypeId', i18n.t('errors:requiredField'));
    }

    return isValid;
  }

  validateStageType(rigOperation: RigOperation, stage: RigOperationStage): boolean {
    let isValid = true;

    if (!stage.wellLifeCycleStageId) {
      isValid = false;

      stage.addError('wellLifeCycleStageId', i18n.t('errors:requiredField'));
    }

    return isValid;
  }

  validateCommercialSpeed(rigOperation: RigOperation, stage: RigOperationStage): boolean {
    let isValid = true;

    if (
      this.rigOperationsService.isDrillingStage(stage.wellLifeCycleStageId) &&
      this.rigOperationsService.rigOperations.length > 1
    ) {
      if (!hasValue(stage.commercialSpeed)) {
        isValid = false;

        stage.addError('commercialSpeed', i18n.t('errors:requiredField'));
      } else if (stage.commercialSpeed <= 0) {
        isValid = false;

        stage.addError('commercialSpeed', i18n.t('newWellForm:Errors.csCannotBeLessThanZero'));
      }
    }

    return isValid;
  }

  validateRigId(rigOperation: RigOperation): boolean {
    let isValid = true;

    if (!rigOperation.rigId) {
      isValid = false;

      rigOperation.addError('rigId', i18n.t('errors:requiredField'));
    }

    return isValid;
  }

  validateSection(rigOperation: RigOperation, stage: RigOperationStage, section: RigOperationSection): boolean {
    let isValid = true;

    const isValidSectionType = this.validateSectionType(rigOperation, stage, section);
    const isValidDuration = this.validateDurationField(rigOperation, stage, section);
    const isValidStartDate = this.validateStartDate(rigOperation, stage, section);
    const isValidEndDate = this.validateEndDate(rigOperation, stage, section);
    const isValidPassingValue = this.validatePassingValueField(rigOperation, stage, section);
    const isValidPassingValueMin = this.validatePassingValueMinField(rigOperation, stage, section);
    const isValidPassingValueMax = this.validatePassingValueMaxField(rigOperation, stage, section);

    if (
      !isValidPassingValue ||
      !isValidDuration ||
      !isValidPassingValueMin ||
      !isValidPassingValueMax ||
      !isValidSectionType ||
      !isValidStartDate ||
      !isValidEndDate
    ) {
      isValid = false;
    }

    return isValid;
  }

  validateStage(rigOperation: RigOperation, stage: RigOperationStage): boolean {
    let isValid = true;

    const isValidStartDate = this.validateStartDate(rigOperation, stage);
    const isValidEndDate = this.validateEndDate(rigOperation, stage);
    const isValidStageType = this.validateStageType(rigOperation, stage);

    if (!isValidStartDate || !isValidEndDate || !isValidStageType) {
      isValid = false;
    }

    if (!stage.sections.length) {
      const isValidPassingValue = this.validatePassingValueField(rigOperation, stage);
      const isValidDuration = this.validateDurationField(rigOperation, stage);
      const isValidCommercialSpeed = this.validateCommercialSpeed(rigOperation, stage);

      if (!isValidPassingValue || !isValidDuration || !isValidCommercialSpeed) {
        isValid = false;
      }
    } else {
      const isValidSections = stage.sections.reduce((isValid, sec) => {
        if (!this.validateSection(rigOperation, stage, sec)) {
          isValid = false;
        }

        return isValid;
      }, true);

      if (!isValidSections) {
        isValid = false;
      }
    }

    return isValid;
  }

  validateRigOperation(rigOperation: RigOperation): boolean {
    let isValid = true;

    const isValidRigId = this.validateRigId(rigOperation);
    const isValidStages = rigOperation.stages.reduce((isValid, s) => {
      if (!this.validateStage(rigOperation, s)) {
        isValid = false;
      }

      return isValid;
    }, true);

    if (!isValidStages || !isValidRigId) {
      isValid = false;
    }

    return isValid;
  }

  validateRigOperations(): boolean {
    return this.rigOperationsService.rigOperations.reduce((isValid, rigOperation) => {
      if (!this.validateRigOperation(rigOperation)) {
        isValid = false;
      }

      return isValid;
    }, true);
  }
}
